import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import ConfirmModal from '../../components/ConfirmModal';
import { useToast } from '../../components/Toast';
import { fetchTipoProdutoById, updateTipoProduto, deleteTipoProduto } from '../../services/tipoProdutoService';
import { withPermissionAndLevel } from '../../components/withPermission';

const initialState = {
  nome: '',
  status: '',
  controlaEstoque: 'Sim',
};

const TipoProdutoEdit = () => {
  const { success, error: toastError, ToastContainer } = useToast();
  const { id } = useParams();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const getTipoProduto = async () => {
      try {
        const data = await fetchTipoProdutoById(id);
        if (data) setForm({ 
          nome: data.nome || '', 
          status: data.status || '',
          controlaEstoque: data.controlaEstoque || 'Sim'
        });
      } catch (err) {
        setError('Erro ao buscar tipo de produto.');
      } finally {
        setLoading(false);
      }
    };
    getTipoProduto();
  }, [id]);

  const validate = () => {
    if (!form.nome.trim()) return 'O nome do tipo de produto é obrigatório.';
    if (form.nome && form.nome.length > 255) return 'O nome deve ter até 255 caracteres.';
    if (form.status && form.status.length > 255) return 'Status deve ter até 255 caracteres.';
    return '';
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setShowConfirm(true);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  if (loading) return <div className="max-w-lg mx-auto mt-10 text-blue-600">Carregando dados...</div>;

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <ToastContainer />
      <h2 className="text-xl font-bold mb-4">Editar Tipo de Produto</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Nome *</label>
          <input
            type="text"
            name="nome"
            value={form.nome}
            onChange={handleChange}
            maxLength={255}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            placeholder="Nome do tipo de produto"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Status *</label>
          <select
            name="status"
            value={form.status}
            onChange={handleChange}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            <option value="">Selecione o status</option>
            <option value="Ativo">Ativo</option>
            <option value="Inativo">Inativo</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Controla Estoque *</label>
          <select
            name="controlaEstoque"
            value={form.controlaEstoque}
            onChange={handleChange}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            <option value="Sim">Sim</option>
            <option value="Não">Não</option>
          </select>
          <p className="text-xs text-gray-600 mt-1">
            Define se produtos deste tipo terão movimentação automática de estoque
          </p>
        </div>
        {error && <div className="text-red-600 text-sm">{error}</div>}
        {loading && <div className="text-blue-600 text-sm">Salvando...</div>}
        {saveSuccess && <div className="text-green-600 text-sm">Tipo de Produto atualizado com sucesso!</div>}
        <div className="flex flex-row gap-1 justify-end mt-2 w-full overflow-x-auto">
          <button type="button" className="p-2 sm:px-4 sm:py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center gap-2 w-10 sm:w-auto justify-center" onClick={() => navigate('/tipo-produto')}>
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button type="submit" className="p-2 sm:px-4 sm:py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 flex items-center gap-2 w-10 sm:w-auto justify-center" aria-label="Salvar">
            <FontAwesomeIcon icon={faSave} />
            <span className="hidden sm:inline">Salvar</span>
          </button>
          <button type="button" className="p-2 sm:px-4 sm:py-2 rounded bg-red-600 text-white font-semibold hover:bg-red-700 flex items-center gap-2 w-10 sm:w-auto justify-center" onClick={handleDelete} disabled={deleting} aria-label="Excluir">
            <FontAwesomeIcon icon={faTrash} />
            <span className="hidden sm:inline">{deleting ? 'Excluindo...' : 'Excluir'}</span>
          </button>
        </div>
      </form>
      <ConfirmModal
        isOpen={showConfirm}
        title="Confirmar Edição"
        message="Deseja realmente salvar as alterações deste tipo de produto?"
        onCancel={() => setShowConfirm(false)}
        onConfirm={async () => {
          setShowConfirm(false);
          setLoading(true);
          setError('');
          setSaveSuccess(false);
          try {
            await updateTipoProduto(id, form);
            setSaveSuccess(true);
            success('Tipo de produto atualizado com sucesso!');
            setTimeout(() => navigate('/tipo-produto'), 1500);
          } catch (err) {
            setError('Erro ao atualizar tipo de produto.');
            toastError('Erro ao atualizar tipo de produto.');
          } finally {
            setLoading(false);
          }
        }}
      />
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Confirmar Exclusão"
        message="Deseja realmente excluir este tipo de produto? Esta ação não poderá ser desfeita."
        confirmType="delete"
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          setShowDeleteConfirm(false);
          setDeleting(true);
          try {
            await deleteTipoProduto(id);
            success('Tipo de produto excluído com sucesso!');
            setTimeout(() => navigate('/tipo-produto'), 1500);
          } catch (err) {
            console.error('Erro ao excluir tipo de produto:', err);
            
            if (err.message.includes('sendo usado')) {
              toastError(err.message);
            } else {
              toastError('Erro ao excluir tipo de produto.');
            }
            setError('Erro ao excluir tipo de produto.');
          } finally {
            setDeleting(false);
          }
        }}
      />
    </div>
  );
};

export default withPermissionAndLevel(TipoProdutoEdit, 'tipo_produto.update', 3, 'Você não tem permissão para editar Tipos de Produto.');
